import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Row,
  Col,
  message,
} from 'antd';
import { SaveOutlined, KeyOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { profileService, UpdateProfileRequest, ChangePasswordRequest } from '../../api/profile';
import { useAuth } from '../../hooks/useAuth';

const { Title } = Typography;

const Settings: React.FC = () => {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileRequest) => profileService.updateProfile(data),
    onSuccess: () => {
      message.success(t('messages.profileUpdated'));
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorUpdatingProfile'));
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordRequest) => profileService.changePassword(data),
    onSuccess: () => {
      message.success(t('messages.passwordChanged'));
      passwordForm.resetFields();
      setIsPasswordFormVisible(false);
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorChangingPassword'));
    },
  });

  // Fill form with user data
  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
    }
  }, [user, profileForm]);

  const handleProfileSubmit = async (values: UpdateProfileRequest) => {
    await updateProfileMutation.mutateAsync(values);
  };

  const handlePasswordSubmit = async (values: ChangePasswordRequest) => {
    await changePasswordMutation.mutateAsync(values);
  };

  const validateCurrentPassword = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(t('validation.enterCurrentPassword'));
    }
    return Promise.resolve();
  };

  const validateNewPassword = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(t('validation.enterNewPassword'));
    }
    if (value.length < 8) {
      return Promise.reject(t('validation.minLength', { count: 8 }));
    }
    return Promise.resolve();
  };

  const validateConfirmPassword = (_: unknown, value: string) => {
    const newPassword = passwordForm.getFieldValue('newPassword');
    if (!value) {
      return Promise.reject(t('validation.confirmNewPassword'));
    }
    if (value !== newPassword) {
      return Promise.reject(t('validation.passwordsNotMatch'));
    }
    return Promise.resolve();
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2}>{t('settings.title')}</Title>

      {/* Блок информации о пользователе */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserOutlined />
            {t('settings.userInfo')}
          </div>
        }
        style={{ marginBottom: 24 }}
      >
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleProfileSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label={t('users.firstName')}
                rules={[{ required: true, message: t('validation.enterName') }]}
              >
                <Input placeholder={t('placeholders.enterName')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label={t('users.lastName')}
                rules={[{ required: true, message: t('validation.enterLastName') }]}
              >
                <Input placeholder={t('placeholders.enterLastName')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label={t('users.email')}
          >
            <Input disabled placeholder={t('placeholders.emailCannotChange')} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={updateProfileMutation.isPending}
            >
              {t('settings.saveChanges')}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Блок смены пароля */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <KeyOutlined />
            {t('settings.passwordChange')}
          </div>
        }
        extra={
          !isPasswordFormVisible && (
            <Button 
              type="primary" 
              ghost
              icon={<KeyOutlined />}
              onClick={() => setIsPasswordFormVisible(true)}
            >
              {t('settings.changePasswordBtn')}
            </Button>
          )
        }
      >
        {!isPasswordFormVisible ? (
          <p style={{ margin: 0, color: '#666' }}>
            {t('settings.changePasswordDesc')}
          </p>
        ) : (
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordSubmit}
          >
            <Form.Item
              name="currentPassword"
              label={t('users.currentPassword')}
              rules={[{ validator: validateCurrentPassword }]}
            >
              <Input.Password placeholder={t('placeholders.enterCurrentPassword')} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="newPassword"
                  label={t('users.newPassword')}
                  rules={[{ validator: validateNewPassword }]}
                >
                  <Input.Password placeholder={t('placeholders.enterNewPassword')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="confirmPassword"
                  label={t('users.confirmPassword')}
                  rules={[{ validator: validateConfirmPassword }]}
                >
                  <Input.Password placeholder={t('placeholders.repeatNewPassword')} />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item>
              <Row gutter={16}>
                <Col>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<KeyOutlined />}
                    loading={changePasswordMutation.isPending}
                  >
                    {t('users.changePassword')}
                  </Button>
                </Col>
                <Col>
                  <Button
                    onClick={() => {
                      setIsPasswordFormVisible(false);
                      passwordForm.resetFields();
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default Settings;
